import { useEffect, useState } from 'react';
import { getFeatureFlagStatus } from '../config/featureFlags';
import { useSongStore as useLocalSongStore } from '../stores/songStore';
import { useSongStoreWithPersistence } from '../stores/songStoreWithPersistence';

/**
 * Hook that selects the appropriate song store based on feature flags.
 * This allows us to gradually roll out server persistence.
 */
export function useSongStoreSelector() {
  // Initialize with the actual feature flag value to avoid flashing
  const [useServerPersistence, setUseServerPersistence] = useState(() => 
    getFeatureFlagStatus('USE_SERVER_PERSISTENCE')
  );
  
  useEffect(() => {
    // Check feature flag on mount and when it might change
    const checkFlag = () => {
      const shouldUseServer = getFeatureFlagStatus('USE_SERVER_PERSISTENCE');
      if (shouldUseServer !== useServerPersistence) {
        setUseServerPersistence(shouldUseServer);
      }
    };
    
    checkFlag();
    
    // Re-check periodically in case feature flag changes
    const interval = setInterval(checkFlag, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, [useServerPersistence]);
  
  // Always call hooks unconditionally to follow Rules of Hooks
  const localStore = useLocalSongStore();
  const persistenceStore = useSongStoreWithPersistence();
  
  // Initialize persistence store when needed
  useEffect(() => {
    if (useServerPersistence && 
        persistenceStore.songs.length === 0 && 
        !persistenceStore.isLoading && 
        !persistenceStore.hasLoadedInitial) {
      persistenceStore.loadSongs();
    }
  }, [useServerPersistence, persistenceStore]);
  
  // Return the appropriate store based on feature flag
  return useServerPersistence ? persistenceStore : localStore;
}

// Export type for consistency
export type SongStore = ReturnType<typeof useSongStoreSelector>;

// Helper function to get the store instance directly
// This is useful for non-component contexts
export function getSongStore() {
  const useServerPersistence = getFeatureFlagStatus('USE_SERVER_PERSISTENCE');
  
  if (useServerPersistence) {
    return useSongStoreWithPersistence;
  }
  
  return useLocalSongStore;
}